import { auth } from '../config/firebase';

const REGION = 'us-central1';
const PROJECT_ID = process.env.REACT_APP_FIREBASE_PROJECT_ID;

/**
 * Invoke a Firebase HTTPS callable without the Functions SDK (avoids
 * "Service functions is not available" in CRA production bundles).
 */
const HTTP_API_NAMES = {
  submitReport: 'apiSubmitReport',
  updateReport: 'apiUpdateReport',
  moderateReport: 'apiModerateReport',
  bulkModerateReports: 'apiBulkModerateReports',
  listReportsForAdmin: 'apiListReportsForAdmin',
  claimNumber: 'apiClaimNumber',
  sendVerificationCode: 'apiSendVerificationCode',
  verifyOwnershipCode: 'apiVerifyOwnershipCode',
  updateNumberSettings: 'apiUpdateNumberSettings',
};

function resolveFunctionUrl(logicalName, cloudFunctionName) {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const useSameOriginProxy =
      host === 'WhoDatNumber.com' ||
      host === 'www.WhoDatNumber.com' ||
      host.endsWith('.WhoDatNumber.pages.dev');
    if (useSameOriginProxy) {
      return `/api/${logicalName}`;
    }
  }
  if (!PROJECT_ID) {
    throw new Error('Firebase project ID is not configured');
  }
  return `https://${REGION}-${PROJECT_ID}.cloudfunctions.net/${cloudFunctionName}`;
}

export async function callFunction(name, data = {}) {
  const functionName = HTTP_API_NAMES[name] || name;
  const url = resolveFunctionUrl(name, functionName);
  const headers = { 'Content-Type': 'application/json' };

  const user = auth.currentUser;
  if (user) {
    headers.Authorization = `Bearer ${await user.getIdToken()}`;
  }

  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ data }),
    });
  } catch (networkErr) {
    throw new Error(
      networkErr.message === 'Failed to fetch'
        ? 'Could not reach the server. Check your connection, disable ad blockers for this site, and try again.'
        : networkErr.message
    );
  }

  let body;
  try {
    body = await response.json();
  } catch {
    throw new Error(`Function ${name} returned an invalid response`);
  }

  if (body.error) {
    const message = body.error.message || `Function ${name} failed`;
    const err = new Error(message);
    err.code = body.error.status;
    if (body.error.existingReportId) err.existingReportId = body.error.existingReportId;
    throw err;
  }

  if (!response.ok) {
    const hint =
      response.status === 403
        ? ' Cloud Function may need public invoker access in Google Cloud Console.'
        : '';
    throw new Error(
      (body.error?.message || body.message || `Function ${functionName} failed (${response.status})`) +
        hint
    );
  }

  return body.result;
}

